import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

configure({ enforceActions: 'always' });

/**
 * Club advent calendar («Julekalender»): the member view, the opened doors' content and the
 * club-admin editor state. Spec: docs/superpowers/specs/2026-11-03-club-advent-calendar-design.md
 */
class AdventCalendarStore extends LocalModel {
    constructor() {
        super('adventCalendar', {
            namePlural: 'adventCalendars',
            namePluralReal: 'adventcalendars',
            sort: '-year',
            limit: 10,
            api: {
                search: {
                    url: '/api/adventcalendars/',
                    params: {},
                },
                load: {
                    url: '/api/adventcalendars/',
                    params: {},
                },
                save: {
                    url: '/api/adventcalendars/',
                    params: {},
                },
                delete: {
                    url: '/api/adventcalendars/',
                    params: {},
                },
            },
        });
    }

    @observable adventCalendars = [];

    @observable adventCalendar = {};

    // Member view (GET /api/adventcalendars/)
    @observable view = null;

    // Opened door content keyed by day (POST /open + /answer results)
    @observable doorContent = {};

    // Day-game top lists keyed by day
    @observable gameTop = {};

    // Admin editor
    @observable adminCalendar = {};

    @observable adminLeaderboard = [];

    @observable answerCounts = {};

    @observable adminTickets = [];

    @observable canDraw = false;

    @observable isLoading = false;

    @observable lastError = '';

    // Platform admins may simulate a December day while testing.
    @observable simulatedDay = 0;

    simulateParams() {
        return this.simulatedDay > 0 ? { today: this.simulatedDay } : {};
    }

    @action
    setSimulatedDay(day) {
        this.updateKeyValue('simulatedDay', parseInt(day, 10) || 0);
    }

    @action
    async loadView() {
        this.updateKeyValue('isLoading', true);
        const response = await util.fetchApi('/api/adventcalendars/', { method: 'GET' }, this.simulateParams());
        if (response?.status === 200) {
            this.updateKeyValue('view', response.data || null);
            this.updateKeyValue('lastError', '');
        } else {
            this.updateKeyValue('lastError', response?.message || 'Could not load');
        }
        this.updateKeyValue('isLoading', false);
        return response;
    }

    @action
    async openDoor(day) {
        const response = await util.fetchApi('/api/adventcalendars/open', { method: 'POST' }, {
            day, ...this.simulateParams(),
        });
        if (response?.status === 200) {
            this.updateKeyValue('doorContent', { ...this.doorContent, [day]: response.data });
            await this.loadView();
        } else {
            this.updateKeyValue('lastError', response?.message || 'Could not open');
        }
        return response;
    }

    @action
    async answer(day, letter) {
        const response = await util.fetchApi('/api/adventcalendars/answer', { method: 'POST' }, {
            day, answer: letter, ...this.simulateParams(),
        });
        if (response?.status === 200) {
            const current = this.doorContent[day] || {};
            const content = { ...(current.content || {}) };
            content.quiz = {
                ...(content.quiz || {}),
                correctAnswer: response.data.correctAnswer,
                explanation: response.data.explanation,
            };
            content.yourAnswer = response.data.yourAnswer;
            this.updateKeyValue('doorContent', {
                ...this.doorContent,
                [day]: {
                    ...current,
                    content,
                    quizAnswered: true,
                    quizCorrect: response.data.correct,
                    progress: response.data.progress,
                },
            });
            await this.loadView();
        }
        return response;
    }

    @action
    async completeChallenge(day) {
        const response = await util.fetchApi('/api/adventcalendars/challenge', { method: 'POST' }, {
            day, ...this.simulateParams(),
        });
        if (response?.status === 200) {
            const current = this.doorContent[day] || {};
            this.updateKeyValue('doorContent', { ...this.doorContent, [day]: { ...current, challengeDone: true } });
            await this.loadView();
        }
        return response;
    }

    @action
    async submitGameScore(day, { score, durationMs }) {
        const response = await util.fetchApi('/api/adventcalendars/game-score', { method: 'POST' }, {
            day, score, durationMs, ...this.simulateParams(),
        });
        if (response?.status === 200) {
            this.updateKeyValue('gameTop', { ...this.gameTop, [day]: response.data.top || [] });
            const current = this.doorContent[day] || {};
            this.updateKeyValue('doorContent', {
                ...this.doorContent,
                [day]: { ...current, gameScore: response.data.best?.gameScore ?? current.gameScore },
            });
        }
        return response;
    }

    // --- admin ---

    @action
    async loadAdmin(year) {
        this.updateKeyValue('isLoading', true);
        const params = year ? { year } : {};
        const response = await util.fetchApi('/api/adventcalendars/admin', { method: 'GET' }, params);
        if (response?.status === 200) {
            this.updateKeyValue('adminCalendar', response.data.calendar || {});
            this.updateKeyValue('adminLeaderboard', response.data.leaderboard || []);
            this.updateKeyValue('answerCounts', response.data.answerCounts || {});
            this.updateKeyValue('adminTickets', response.data.tickets || []);
            this.updateKeyValue('canDraw', !!response.data.canDraw);
            this.updateKeyValue('lastError', '');
        } else {
            this.updateKeyValue('lastError', response?.message || 'Could not load');
        }
        this.updateKeyValue('isLoading', false);
        return response;
    }

    @action
    async editDoor({ uuidv4, key, value }) {
        return this.editArrayObject({
            field: 'doors',
            key,
            value,
            id: this.adminCalendar.id,
            uuidv4,
            objectName: 'adminCalendar',
        });
    }

    @action
    async editSponsor({ uuidv4, key, value }) {
        return this.editArrayObject({
            field: 'sponsors',
            key,
            value,
            id: this.adminCalendar.id,
            uuidv4,
            objectName: 'adminCalendar',
        });
    }

    @action
    async addSponsor({ name, link }) {
        const response = await util.fetchApi(`/api/adventcalendars/${this.adminCalendar.id}/sponsors`, { method: 'POST' }, { name, link });
        if (response?.status === 202) {
            this.updateKeyValue('adminCalendar', response.data.adventCalendar || this.adminCalendar);
        }
        return response;
    }

    @action
    async removeSponsor(uuidv4) {
        const response = await util.fetchApi(`/api/adventcalendars/${this.adminCalendar.id}/sponsors/${uuidv4}`, { method: 'DELETE' }, {});
        if (response?.status === 202) {
            this.updateKeyValue('adminCalendar', response.data.adventCalendar || this.adminCalendar);
        }
        return response;
    }

    @action
    async editPrize({ uuidv4, key, value }) {
        return this.editArrayObject({
            field: 'prizes',
            key,
            value,
            id: this.adminCalendar.id,
            uuidv4,
            objectName: 'adminCalendar',
        });
    }

    @action
    async addPrize({ title, sponsor }) {
        const response = await util.fetchApi(`/api/adventcalendars/${this.adminCalendar.id}/prizes`, { method: 'POST' }, { title, sponsor });
        if (response?.status === 202) {
            this.updateKeyValue('adminCalendar', response.data.adventCalendar || this.adminCalendar);
        }
        return response;
    }

    @action
    async removePrize(uuidv4) {
        const response = await util.fetchApi(`/api/adventcalendars/${this.adminCalendar.id}/prizes/${uuidv4}`, { method: 'DELETE' }, {});
        if (response?.status === 202) {
            this.updateKeyValue('adminCalendar', response.data.adventCalendar || this.adminCalendar);
        } else {
            this.updateKeyValue('lastError', response?.message || 'Could not remove');
        }
        return response;
    }

    // Draw one prize (uuidv4) or all undrawn prizes. Returns the API response ({ drawn, skipped }).
    @action
    async drawPrizes({ uuidv4, redraw } = {}) {
        const body = { ...this.simulateParams() };
        if (uuidv4) {
            body.uuidv4 = uuidv4;
        }
        if (redraw) {
            body.redraw = true;
        }
        const response = await util.fetchApi(`/api/adventcalendars/${this.adminCalendar.id}/prizes/draw`, { method: 'POST' }, body);
        if (response?.status === 200) {
            this.updateKeyValue('adminCalendar', response.data.adventCalendar || this.adminCalendar);
            this.updateKeyValue('lastError', '');
        } else {
            this.updateKeyValue('lastError', response?.message || 'Could not draw');
        }
        return response;
    }

    @action
    async undoPrize(uuidv4) {
        const response = await util.fetchApi(`/api/adventcalendars/${this.adminCalendar.id}/prizes/undo`, { method: 'POST' }, { uuidv4 });
        if (response?.status === 202) {
            this.updateKeyValue('adminCalendar', response.data.adventCalendar || this.adminCalendar);
        }
        return response;
    }

    @action
    async suggestQuiz(day, topic = '') {
        const response = await util.fetchApi(`/api/adventcalendars/${this.adminCalendar.id}/suggest-quiz`, { method: 'POST' }, { day, topic });
        return response?.status === 200 ? response.data.quiz : null;
    }

    @action
    async saveTitle(title) {
        const response = await util.fetchApi(`/api/adventcalendars/${this.adminCalendar.id}`, { method: 'PATCH' }, { title });
        if (response?.status === 202) {
            this.updateKeyValue('adminCalendar', response.data.adventCalendar || this.adminCalendar);
        }
        return response;
    }
}

const store = new AdventCalendarStore();
export default store;
